import { CircleAlert, CircleCheck, CircleHelp, Clock3, ExternalLink, Scale, Share2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, shareLink } from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const LOOK = {
  supported: { label: "Supported by approved sources", icon: CircleCheck, cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  contradicted: { label: "Contradicted by approved sources", icon: CircleAlert, cls: "bg-rose-50 text-rose-800 border-rose-200" },
  mixed: { label: "Approved sources disagree", icon: Scale, cls: "bg-amber-50 text-amber-800 border-amber-300" },
  unverified: { label: "Not enough evidence yet", icon: CircleHelp, cls: "bg-stone-100 text-stone-700 border-stone-300" },
  queued: { label: "Not yet checked", icon: Clock3, cls: "bg-white text-slate-500 border-slate-200" },
};
const VERDICT = { SUPPORTED: "Supported", CONTRADICTED: "Contradicted", MIXED: "Sources disagree", "INSUFFICIENT EVIDENCE": "Not enough evidence yet", QUEUED: "Not yet checked" };

const shareClaim = async (c) => {
  try {
    const { data } = await api.post("/share", { kind: "claim", item_id: c.id, channel: "link" });
    if ((await shareLink(`/api/public/og/claim/${c.id}?ref=${data.ref}`)) === "copied") toast.success("Link copied. Anyone can open it, no account needed");
  } catch (e) { toast.error(errMsg(e)); }
};

const ClaimRow = ({ c, i, testid }) => (
  <li className="space-y-1 border-t border-border pt-2 first:border-0 first:pt-0" data-testid={`${testid}-claim-${i}`}>
    <p className="text-sm text-slate-800">"{c.text}"</p>
    <p className="text-xs font-semibold text-slate-600">{VERDICT[c.verdict] || c.verdict}{c.reused && <span className="font-normal text-slate-400"> · from Middle's fact database</span>}</p>
    {c.explanation && <p className="text-xs text-slate-500">{c.explanation}</p>}
    {(c.sources || []).map((s) => (
      <a key={s.id} href={s.url || undefined} target="_blank" rel="noreferrer" className="flex items-start gap-1 text-xs text-[hsl(var(--facts))] hover:underline">
        <ExternalLink size={11} className="mt-0.5 shrink-0" />{s.source ? `${s.source}: ` : ""}{s.title}{s.stance === "conflicts" && " (conflicts)"}
      </a>
    ))}
    {c.id && c.verdict && c.verdict !== "QUEUED" && (
      <button onClick={() => shareClaim(c)} className="inline-flex items-center gap-1 rounded-full border border-border px-2.5 py-0.5 text-[11px] font-medium text-slate-600 transition-colors hover:border-slate-400" data-testid={`${testid}-share-${i}`}>
        <Share2 size={11} />Share fact card
      </button>
    )}
  </li>
);

export const FactCheckTag = ({ fc, testid = "fact-check" }) => {
  if (!fc?.claims?.length && fc?.status !== "queued") return null;
  const look = LOOK[fc.status === "pending" ? "queued" : fc.status];
  if (!look) return null;
  const Icon = look.icon;
  return (
    <Popover>
      <PopoverTrigger data-testid={`${testid}-tag`} className={`mt-2 inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[11px] font-medium transition-colors hover:brightness-95 ${look.cls}`}>
        <Icon size={12} />{look.label}
      </PopoverTrigger>
      <PopoverContent className="w-80 space-y-2" data-testid={`${testid}-details`}>
        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Middle fact check</p>
        {fc.claims?.length ? <ul className="space-y-2">{fc.claims.map((c, i) => <ClaimRow key={c.id || i} c={c} i={i} testid={testid} />)}</ul>
          : <p className="text-sm text-slate-600">This may contain a factual claim that hasn't been checked yet.</p>}
        <p className="text-[11px] text-slate-400">Checked only against Middle's approved sources. Claims are checked when they matter most: risky topics or posts that spread widely.</p>
      </PopoverContent>
    </Popover>
  );
};
